// src/pages/POS/components/CreatePosForm.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, DatePicker, Typography, Flex, Grid, Radio , Button, Drawer, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";
import InputField from "../../../components/Input/Input";
import dayjs from "dayjs";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import {
  fetchPosVoucherNumber,
  fetchCustomers,
  fetchSalesAccounts,
  fetchTaxAccounts,
  fetchDiscountAccounts,
  fetchTransportAccounts
} from "../posThunk";
import {
  fetchItems,
  fetchPreviousDue,
} from "../../../store/commonThunks/lookups";
import {
  calculateTotals,
  setCustomer,
  setFooterValues,
  setProductImage,
  setTaxMode,
} from "../posSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { selectPreviousDue } from "../../../store/commonSelectors/lookupsSelectors";
import CustomerDrawer from "../../Customers/components/CustomerDrawer";


const { Title } = Typography;
const { useBreakpoint } = Grid;

const CreatePosForm = ({ authInfo }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const screens = useBreakpoint();
  const location = useLocation();

  const {
    voucherNumber,
    entryDate,
    customer,
    customers,
    taxMode,
    productImage,
    selectedSalesAcc,
    footerValues,
  } = useSelector((s) => s.pos);
  const previousDue = useSelector(selectPreviousDue);
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!voucherNumber) {
      dispatch(fetchPosVoucherNumber());
    }
  }, [voucherNumber])

  useEffect(() => {
    dispatch(fetchPosVoucherNumber());
    dispatch(fetchCustomers());
    dispatch(fetchSalesAccounts());
    dispatch(fetchTaxAccounts());
    dispatch(fetchDiscountAccounts());
    dispatch(fetchTransportAccounts());
  }, [dispatch]);

  const handleCustomerSelect = (obj) => {
    if (!obj) {
      dispatch(setCustomer(null));
      return;
    }

    if (obj === "add_new") {
      setCustomerDrawerOpen(true);
      return;
    }

    const customer = obj.raw || obj; // ✅ normalize

    dispatch(setCustomer(customer));
    dispatch(fetchItems());
    dispatch(fetchPreviousDue(customer.acc_id));
  };

  return (
    <div>
      {/* <Title level={3}>POS Entry</Title> */}
      <Flex justify="space-between" align="center">
      <Title level={3} >
        POS Entry
      </Title>

      <Tooltip title="Help">
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
          onClick={() => setHelpOpen(true)}
        />
      </Tooltip>
    </Flex>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1200px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item
              label="Search Customer"
              name="search_customer"
              rules={[{ required: true, message: "Select customer" }]}
            >
              <SearchListAutoComplete
                entityType="customer"
                placeholder="Customer"
                optionsList={(customers || []).map((c) => ({
                  sale_id: c.acc_id,
                  display_text: c.acc_name || c.institution_name,
                  raw: c,
                }))}
                value={
                  customer
                    ? { value: customer.acc_name, label: customer.acc_name }
                    : undefined
                }
                // onSelect={(obj) => handleCustomerSelect(obj?.raw)}
                onSelect={handleCustomerSelect}
                showAddNew={true}
              />
            </Form.Item>

            <Form.Item label="Voucher No.">
              <InputField value={voucherNumber} disabled />
            </Form.Item>

            <Form.Item label="Institution Name">
              <InputField value={customer?.institution_name || ""} disabled />
            </Form.Item>

            <Form.Item label="Contact No">
              <InputField value={customer?.contact_no || ""} disabled />
            </Form.Item>
          </Flex>
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item label="Address">
              <InputField value={customer?.address || ""} disabled />
            </Form.Item>
            <Form.Item label="Entry Date">
              <DatePicker
                value={entryDate ? dayjs(entryDate) : null}
                disabled // ✅ disabled
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
              />
            </Form.Item>
            <Form.Item label="Previous Due">
              <InputField value={previousDue?.toFixed(2)} disabled />
            </Form.Item>

            {/* <Form.Item label="Product Image">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = () => {
                      dispatch(setProductImage(reader.result));
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </Form.Item> */}
            {taxMode != 'on_total' &&
              <>
                <Form.Item label="Discount">
                  <Radio.Group
                    options={[
                      { label: "On Total", value: "on_total" },
                      { label: "On Individual Item", value: "on_item" },
                    ]}
                    onChange={(e) => {
                      dispatch(setTaxMode(e.target.value));
                      dispatch(calculateTotals({ authInfo }));
                    }}
                    value={taxMode}
                  />
                </Form.Item>
              </>}
          </Flex>
        </Flex>
      </Form>
      <CustomerDrawer
        open={customerDrawerOpen}
        onClose={() => setCustomerDrawerOpen(false)}
        authInfo={authInfo}
        onCreated={(customer) => {
          if (!customer) return;

          // 1️⃣ close drawer
          setCustomerDrawerOpen(false);

          // 2️⃣ update redux (this drives the UI)
          dispatch(setCustomer(customer));

          // 3️⃣ refresh customer list
          dispatch(fetchCustomers());

          // 4️⃣ fetch dependent data
          dispatch(fetchItems());
          dispatch(fetchPreviousDue(customer.acc_id));

          // 5️⃣ optional: sync form field (not required if controlled)
          form.setFieldValue("search_customer", {
            value: customer.acc_name,
            label: customer.acc_name,
            display_text: customer.acc_name,
            raw: customer,
          });
        }}
      />
      <Drawer
        title="POS Entry Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            POS Entry
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The POS module allows users to record and manage sales transactions
            efficiently within the ERP system. Billing, inventory, and account
            information are updated automatically during the sales process.
          </p>

          {/* AUTOCART SECTION */}
          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>POS → AutoCart</strong>

            <p style={{ marginTop: "10px", color: "#666" }}>
              AutoCart helps users create sales invoices quickly by automatically
              adding items through barcode scanning or quick product selection.
            </p>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Open <strong>POS → AutoCart</strong></li>
              <li>Select or create the customer</li>
              <li>Add items using barcode scan or quick selection</li>
              <li>Verify quantity, price, and totals</li>
              <li>Apply discounts if applicable.</li>
              <li>Review invoice details</li>
              <li>Click <strong>Save</strong> to complete the transaction</li>
            </ol>
          </div>

          {/* MANUAL CART SECTION */}
          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>POS → Manual Cart</strong>

            <p style={{ marginTop: "10px", color: "#666" }}>
              Manual Cart allows users to manually enter product and transaction
              details step by step during sales entry.
            </p>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Open <strong>POS → Manual Cart</strong></li>
              <li>Select or create the customer</li>
              <li>Manually add products/items</li>
              <li>Enter quantity and verify pricing</li>
              <li>Apply discounts if applicable.</li>
              <li>Review invoice totals and customer details</li>
              <li>Click <strong>Save</strong> to complete the transaction</li>
            </ol>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default CreatePosForm;
